import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  AdminEnrollmentFilterParams,
  AdminEnrollmentsResponse,
  AdminEnrollmentItem,
  ManualEnrollmentPayload,
  UpdateEnrollmentPayload,
  enrollmentService,
} from "../../services/enrollmentService";

/**
 * TanStack Query hook for SuperAdmin Enrollments Directory & KPIs
 * Incorporates 3-minute staleTime cache to prevent unnecessary Railway backend calls.
 */
export const useAdminEnrollmentsQuery = (params?: AdminEnrollmentFilterParams) => {
  return useQuery<AdminEnrollmentsResponse>({
    queryKey: queryKeys.enrollments.adminList(params),
    queryFn: ({ signal }) => enrollmentService.getAdminEnrollments({ ...params, signal }),
    staleTime: 1000 * 60 * 3, // 3 minutes cache freshness
    gcTime: 1000 * 60 * 10,   // 10 minutes cache retention
  });
};

/**
 * Query hook for a specific student enrollment dossier
 */
export const useAdminEnrollmentDetailQuery = (
  id?: number | string,
  mode: "online" | "onsite" = "online"
) => {
  return useQuery<AdminEnrollmentItem>({
    queryKey: queryKeys.enrollments.adminDetail(id || "", mode),
    queryFn: ({ signal }) => enrollmentService.getEnrollmentDetail(id!, mode, signal),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 2,
  });
};

/**
 * Mutation hook to update enrollment status, payment clearance, or graduation
 */
export const useUpdateAdminEnrollmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      mode,
      data,
    }: {
      id: number | string;
      mode: "online" | "onsite";
      data: UpdateEnrollmentPayload;
    }) => enrollmentService.updateEnrollment(id, mode, data),
    onSuccess: () => {
      // Invalidate all enrollment queries to sync tables and stats seamlessly
      queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
    },
  });
};

/**
 * Mutation hook for manual student enrollment by SuperAdmin
 */
export const useManualEnrollmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ManualEnrollmentPayload) => enrollmentService.manualEnroll(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
    },
  });
};

/**
 * Mutation hook to withdraw or delete an enrollment record
 */
export const useDeleteAdminEnrollmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, mode }: { id: number | string; mode: "online" | "onsite" }) =>
      enrollmentService.deleteEnrollment(id, mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
    },
  });
};
